import { useEffect, useState } from 'react';
import { SubmissionStatusId, type SubmissionDetail } from '@/core/models/filing.models';
import { notifyError } from '@/core/notify/notify';
import { filingService } from '@/core/services/filing';
import { Button } from '@/kit/Button';
import { Icon } from '@/kit/Icon';
import { DialogActions, DialogClose, DialogContent, type DialogComponentProps } from '@/kit/dialog';
import { formatDateTime } from './shared';

export interface SubmissionResponseData {
  id: string;
  refNo: string;
}

/** What the dialog closes with: "retry" opens the submission again to be sent once more. */
export type SubmissionResponseResult = 'retry' | undefined;

/** Messages Companies House or the filing service gave that mean something went wrong. */
const isProblem = (severity: string) => /blocking|error/i.test(severity);

/**
 * The response to a submission, as Capium 3.0's clickable ERROR status showed it: the latest attempt's messages
 * (Companies House's rejection reasons, or why the submission could not be sent), with the response as a PDF and, when
 * the submission can be sent again, Retry.
 */
export function SubmissionResponse({
  data,
  dialogRef,
}: DialogComponentProps<SubmissionResponseData, SubmissionResponseResult>) {
  const [detail, setDetail] = useState<SubmissionDetail | null>(null);

  useEffect(() => {
    if (!data) {
      return;
    }
    filingService.getSubmission(data.id).then(setDetail, (error) => notifyError(error));
  }, [data]);

  const attempt = detail?.attempts[0];
  const messages = attempt?.messages ?? [];

  return (
    <div className="mat-dialog-wrapper submission-response">
      <DialogContent>
        <div className="main-contain">
          <div className="sidenav-header">
            <div className="d-flex align-items-center justify-space-between">
              <h2>
                <span>Response - {data?.refNo}</span>
              </h2>
              <DialogClose>
                <Icon name="close" className="close" />
              </DialogClose>
            </div>
          </div>
        </div>
        {!detail && <p className="font-size-14">Loading the response...</p>}
        {detail && (
          <div className="font-size-14">
            <p className="mb-5">
              <strong>Status:</strong>{' '}
              <span
                className={
                  detail.statusId === SubmissionStatusId.Rejected || detail.statusId === SubmissionStatusId.Failed
                    ? 'text-red'
                    : ''
                }
              >
                {detail.status}
              </span>
            </p>
            {!attempt && <p>This submission has not been sent to Companies House yet.</p>}
            {attempt && (
              <>
                <p className="mb-5">
                  <strong>Attempt {attempt.attemptNo}:</strong> started {formatDateTime(attempt.startedAtUtc)}
                  {attempt.completedAtUtc ? `, answered ${formatDateTime(attempt.completedAtUtc)}` : ''}
                  {attempt.submissionReference ? ` - reference ${attempt.submissionReference}` : ''}
                  {attempt.isSandbox ? ' (sandbox: nothing was sent to Companies House)' : ''}
                </p>
                {messages.length === 0 ? (
                  <p>No message was recorded for this attempt.</p>
                ) : (
                  <ul className="submission-response-messages">
                    {messages.map((m, index) => (
                      <li key={`${m.code}-${index}`} className={isProblem(m.severity) ? 'text-red' : ''}>
                        {m.message}
                        {m.code && <span className="text-gray"> ({m.code})</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <div className="d-flex gap-15">
          {detail?.canSubmit && attempt && (
            <Button className="action-button primary-button" onClick={() => dialogRef.close('retry')}>
              <Icon name="refresh" />
              Retry
            </Button>
          )}
          <Button
            className="action-button secondary-button"
            disabled={!detail}
            onClick={() => data && void filingService.downloadSubmission(data.id)}
          >
            <Icon name="picture_as_pdf" outlined className="icon-color-pdf" />
            Download response
          </Button>
          <DialogClose as="button" type="button" className="action-button secondary-button">
            Close
          </DialogClose>
        </div>
      </DialogActions>
    </div>
  );
}
